/**
 * "+ Criar": a criação rápida, agora na barra superior (CERES-UX-001).
 *
 * Saiu do cabeçalho do painel para valer em toda jornada. Cada destino abre a
 * tela correspondente já com o formulário de criação aberto (`?novo=1`) — o
 * mesmo mecanismo de sempre, então há um único caminho de criação para manter.
 * Cada opção exige a permissão de criar E a capacidade do módulo.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../../lib/session';
import { t, type ChaveDeTexto } from '../../lib/i18n';
import {
  IconBuilding,
  IconCalendar,
  IconChevronDown,
  IconNote,
  IconPlus,
  IconTarget,
  IconUsers,
} from '../ui';

interface OpcaoDeCriacao {
  chave: ChaveDeTexto;
  para: string;
  permissao: string;
  capacidade: string;
  icone: (props: { className?: string }) => JSX.Element;
  grupo: 'REGISTROS' | 'DOCUMENTOS';
}

const OPCOES: OpcaoDeCriacao[] = [
  {
    chave: 'comum.empresa',
    para: '/empresas?novo=1',
    permissao: 'account:create',
    capacidade: 'companies',
    icone: IconBuilding,
    grupo: 'REGISTROS',
  },
  {
    chave: 'contatos.contato',
    para: '/contatos?novo=1',
    permissao: 'contact:create',
    capacidade: 'contacts',
    icone: IconUsers,
    grupo: 'REGISTROS',
  },
  {
    chave: 'oportunidade.titulo',
    para: '/oportunidades?novo=1',
    permissao: 'opportunity:create',
    capacidade: 'opportunities',
    icone: IconTarget,
    grupo: 'REGISTROS',
  },
  {
    chave: 'painel.atividade',
    para: '/atividades?novo=1',
    permissao: 'activity:create',
    capacidade: 'activities',
    icone: IconCalendar,
    grupo: 'REGISTROS',
  },
  {
    chave: 'propostas.criarNoCeres',
    para: '/propostas/gerar',
    permissao: 'proposal:create',
    capacidade: 'proposals',
    icone: IconNote,
    grupo: 'DOCUMENTOS',
  },
  {
    chave: 'propostas.registrar',
    para: '/propostas?registrar=1',
    permissao: 'proposal:create',
    capacidade: 'proposal_registry',
    icone: IconNote,
    grupo: 'DOCUMENTOS',
  },
];

export function MenuCriar(): JSX.Element | null {
  const { can, hasFeature } = useSession();
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (e: MouseEvent): void => {
      if (containerRef.current !== null && !containerRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    };
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const visiveis = OPCOES.filter((o) => can(o.permissao) && hasFeature(o.capacidade));
  if (visiveis.length === 0) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="btn-primary h-10 rounded-lg px-3.5"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
      >
        <IconPlus />
        {t('painel.criar')}
        <IconChevronDown className="h-3.5 w-3.5" />
      </button>
      {aberto && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-64 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-pop"
        >
          {(['REGISTROS', 'DOCUMENTOS'] as const).map((grupo) => {
            const opcoes = visiveis.filter((opcao) => opcao.grupo === grupo);
            if (opcoes.length === 0) return null;
            return (
              <div
                key={grupo}
                className="py-1"
                role="group"
                aria-label={t(
                  grupo === 'REGISTROS' ? 'criar.grupoRegistros' : 'criar.grupoDocumentos',
                )}
              >
                <p className="px-3 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
                  {t(grupo === 'REGISTROS' ? 'criar.grupoRegistros' : 'criar.grupoDocumentos')}
                </p>
                {opcoes.map((opcao) => (
                  <Link
                    key={opcao.para}
                    to={opcao.para}
                    role="menuitem"
                    className="flex min-h-[2.5rem] items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-surface-sunken"
                    onClick={() => setAberto(false)}
                  >
                    <opcao.icone className="h-4 w-4 shrink-0 text-brand-600" />
                    {t(opcao.chave)}
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
